import Linkify from "linkify-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FeedItem from "../../Feed/FeedItem";
import PostLinkPreview from "../../Feed/PostLinkPreview";
import LinkPreview from "../../linkPreview/LinkPreview";

const extractYouTubeVideo = (text) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^\s&#?]+)/;
  const match = text.match(regex);

  if (!match) return null;

  const videoId = match[1];

  return {
    videoId,
    thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    originalUrl: `https://www.youtube.com/watch?v=${videoId}`,
  };
};

/**
 * Detects if text contains a link to a post
 * @param {string} text - The text to check
 * @returns {string|null} - The post ID if found, null otherwise
 */
const extractPostLink = (text) => {
  if (!text) return null;

  // Match patterns like:
  // - loonlog.ca/post/123456
  // - www.loonlog.ca/post/123456
  // - https://loonlog.ca/post/123456
  // - http://localhost:3000/post/123456
  // - https://localhost:3000/post/123456
  // - /post/123456
  const postLinkRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:loonlog\.ca|localhost:3000)\/post\/([a-f0-9]{24})|\/post\/([a-f0-9]{24})/i;
  const match = text.match(postLinkRegex);

  if (match) {
    return match[1] || match[2]; // Return the post ID
  }

  return null;
};
const extractExternalLink = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex);
  if (!matches) return null;

  // Filter out internal links
  const external = matches.find(
    (u) =>
      !u.includes("loonlog.ca") &&
      !u.includes("localhost:3000") &&
      !u.includes("127.0.0.1"),
  );

  return external || null;
};
const processContent = (text) => {
  if (!text) return text;
  const parts = text.split(/(\s+)/);
  return parts.map((part, i) => {
    const mentionMatch = part.match(/^(@[a-zA-Z0-9_.]*[a-zA-Z0-9_]+)(\W.*)?$/);
    if (mentionMatch) {
      return (
        <span key={i}>
          <Link
            to={`/profile/${mentionMatch[1].slice(1)}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: "#d80621" }}
          >
            {mentionMatch[1]}
          </Link>
          {mentionMatch[2]}
        </span>
      );
    }
    const hashtagMatch = part.match(/^(#[a-zA-Z0-9_]+)(\W.*)?$/);
    if (hashtagMatch) {
      return (
        <span key={i}>
          <Link
            to={`/hashtag/${hashtagMatch[1].slice(1)}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: "#d80621" }}
          >
            {hashtagMatch[1]}
          </Link>
          {hashtagMatch[2]}
        </span>
      );
    }
    return part;
  });
};

function PostBody({ body, subject, originalPost, noPadding }) {
  const video = body ? extractYouTubeVideo(body) : null;
  const postLinkId = body ? extractPostLink(body) : null;
  const [expanded, setExpanded] = useState(false);
  const externalLink = useMemo(
    () => (body ? extractExternalLink(body) : null),
    [body],
  );

  return (
    <div>
      {subject && (
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "inherit",
          }}
        >
          {subject}
        </h4>
      )}
      {body &&
        (() => {
          // const maxChars = 180;
          const maxChars = 1000;
          const isLong = body?.length > maxChars;
          const shouldTruncate = isLong && !expanded;
          const displayText = shouldTruncate
            ? body?.slice(0, maxChars) + ""
            : body;

          return (
            <p
              className={`post__body word-wrap ${noPadding ? "no-padding" : ""}`}
            >
              <Linkify
                options={{
                  rel: "noopener noreferrer",
                  target: "_blank",
                  attributes: {
                    onClick: (e) => e.stopPropagation(),
                  },
                }}
              >
                {processContent(displayText)}
              </Linkify>
              {shouldTruncate && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  style={{
                    color: "#1DA1F2",
                    cursor: "pointer",
                    marginLeft: "5px",
                    fontWeight: 500,
                  }}
                >
                  Show more
                </span>
              )}
            </p>
          );
        })()}

      {video && (
        <a
          href={video.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={video.thumbnail}
            alt="YouTube thumbnail"
            className="post-media-item"
          />
        </a>
      )}

      {postLinkId && <PostLinkPreview postId={postLinkId} />}
      {/* 🌍 External Link Preview */}
      {!postLinkId && !video && externalLink && (
        <LinkPreview url={externalLink} />
      )}

      {originalPost &&
        (originalPost.originalPost ? (
          <div>
            <Link to={`post/${originalPost._id}`}>post/{originalPost._id}</Link>

            {originalPost.subject && (
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                {originalPost.subject}
              </div>
            )}
            {originalPost.body}
          </div>
        ) : (
          <FeedItem post={originalPost} isReposted={originalPost} />
        ))}
    </div>
  );
}

export default PostBody;

